"use strict";
// packages/shared-schemas/src/approvedVsSettledV2.ts
//
// Approved vs Settled v2 page: tenant flag, monthly summary and the
// "Settled, awaiting payment" store.
//
// Design: docs/handovers/2026-10-07-APPROVED-VS-SETTLED-PAGE-HANDOVER.md
// (sections 3 to 5) and the founder decisions of 2026-10-09:
//   * the v2 page is selected per tenant by featureFlags.approved_vs_settled_v2;
//     absent / anything else = today's page, unchanged;
//   * Paid is never stored or set by a person: it is read from the deal -> loan
//     link (a statement match). There is no manual payment action;
//   * awaiting-payment statuses are this page's own store, keyed by deal id,
//     and never part of the link contract (dealLoanLink.ts);
//   * Net of offset counts only loans whose CRM deal is fully linked inside the
//     caller's view; everything else is reported as excluded, never mixed in.
Object.defineProperty(exports, "__esModule", { value: true });
exports.AwaitingPaymentActionRequestSchema = exports.AwaitingPaymentSearchLoansRequestSchema = exports.AWAITING_PAYMENT_SEARCH_MAX = exports.AwaitingPaymentAddNoteRequestSchema = exports.AwaitingPaymentSetStatusRequestSchema = exports.AwaitingPaymentDetailRequestSchema = exports.AwaitingPaymentListRequestSchema = exports.AwaitingPaymentStatusDocSchema = exports.AWAITING_PAYMENT_NOTE_MAX = exports.NOT_EXPECTED_REASONS = exports.AWAITING_PAYMENT_SET_STATUSES = exports.APPROVED_VS_SETTLED_V2_MODES = exports.APPROVED_VS_SETTLED_V2_FLAG = void 0;
exports.parseApprovedVsSettledV2Mode = parseApprovedVsSettledV2Mode;
exports.netOfOffsetRatio = netOfOffsetRatio;
const zod_1 = require("zod");
const callableParam_1 = require("./callableParam");
// ────────────────────────────────────────────────────────────────────────
// Tenant flag: featureFlags.approved_vs_settled_v2 (default off)
// ────────────────────────────────────────────────────────────────────────
exports.APPROVED_VS_SETTLED_V2_FLAG = 'approved_vs_settled_v2';
exports.APPROVED_VS_SETTLED_V2_MODES = ['off', 'enabled'];
/**
 * Parse `companies/{id}.featureFlags.approved_vs_settled_v2`. Only the exact
 * string 'enabled' selects the v2 page; a missing flag, a boolean, a typo or a
 * read failure is 'off' (today's page, byte-identical).
 */
function parseApprovedVsSettledV2Mode(raw) {
    return raw === 'enabled' ? 'enabled' : 'off';
}
/**
 * Net of offset = paid on (initial + top-ups) / New Funds, over the same deals.
 * Returns null when there is no denominator. Pure; the page recomputes totals
 * over filtered rows with the same function.
 */
function netOfOffsetRatio(paidOnCents, newFundsCents) {
    if (paidOnCents === null || newFundsCents === null || newFundsCents <= 0)
        return null;
    return paidOnCents / newFundsCents;
}
// ────────────────────────────────────────────────────────────────────────
// Awaiting-payment statuses (Firestore, this page's own store)
//   companies/{companyId}/awaiting_payment_status/{dealId}
//   companies/{companyId}/awaiting_payment_status/{dealId}/events/{eventId}
// Server-only: firestore.rules grants clients nothing on either path.
// ────────────────────────────────────────────────────────────────────────
/** Statuses a person may set. 'paid' is derived from the link, never stored. */
exports.AWAITING_PAYMENT_SET_STATUSES = ['awaiting', 'chase', 'not_expected'];
exports.NOT_EXPECTED_REASONS = [
    'declined_after_settlement',
    'paid_to_another_aggregator',
    'commission_waived',
    'other',
];
exports.AWAITING_PAYMENT_NOTE_MAX = 2000;
const DealIdSchema = zod_1.z.string().min(1).max(64);
const CivilDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const NoteSchema = zod_1.z.string().trim().min(1).max(exports.AWAITING_PAYMENT_NOTE_MAX);
const MonthSchema = zod_1.z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'must be in YYYY-MM format');
/** Stored status document. */
exports.AwaitingPaymentStatusDocSchema = zod_1.z.object({
    dealId: DealIdSchema,
    status: zod_1.z.enum(exports.AWAITING_PAYMENT_SET_STATUSES),
    reason: zod_1.z.enum(exports.NOT_EXPECTED_REASONS).nullable(),
    followUpDate: CivilDateSchema.nullable(),
    updatedByUid: zod_1.z.string().min(1),
    updatedByName: zod_1.z.string(),
    /** Monotonic, starts at 1. Writes carry the revision they were based on. */
    revision: zod_1.z.number().int().positive(),
});
// ── awaitingPaymentAction request (one callable, discriminated by action) ──
const ListFilters = {
    companyId: zod_1.z.string().min(1),
    startMonth: MonthSchema,
    endMonth: MonthSchema,
    officeId: (0, callableParam_1.callableOptional)(zod_1.z.string().min(1)),
    brokerId: (0, callableParam_1.callableOptional)(zod_1.z.string().min(1)),
    templateId: (0, callableParam_1.callableOptional)(zod_1.z.string().min(1)),
    entityCode: (0, callableParam_1.callableOptional)(zod_1.z.string().min(1)),
};
exports.AwaitingPaymentListRequestSchema = zod_1.z.object({ action: zod_1.z.literal('list'), ...ListFilters });
exports.AwaitingPaymentDetailRequestSchema = zod_1.z.object({
    action: zod_1.z.literal('detail'),
    companyId: zod_1.z.string().min(1),
    dealId: DealIdSchema,
});
exports.AwaitingPaymentSetStatusRequestSchema = zod_1.z
    .object({
    action: zod_1.z.literal('setStatus'),
    companyId: zod_1.z.string().min(1),
    dealId: DealIdSchema,
    status: zod_1.z.enum(exports.AWAITING_PAYMENT_SET_STATUSES),
    reason: (0, callableParam_1.callableOptional)(zod_1.z.enum(exports.NOT_EXPECTED_REASONS)),
    note: (0, callableParam_1.callableOptional)(NoteSchema),
    followUpDate: (0, callableParam_1.callableOptional)(CivilDateSchema),
    /** Revision the caller was looking at; 0 when no status was stored yet. */
    expectedRevision: zod_1.z.number().int().min(0),
})
    .superRefine((r, ctx) => {
    if (r.status === 'chase' && !r.note) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['note'], message: 'A note is required to chase.' });
    }
    if (r.status === 'not_expected' && !r.reason) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['reason'], message: 'Choose why no payment is expected.' });
    }
    if (r.status === 'not_expected' && r.reason === 'other' && !r.note) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['note'], message: 'Add a note explaining the reason.' });
    }
    if (r.status !== 'not_expected' && r.reason) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['reason'], message: 'A reason applies only to Not expected.' });
    }
    if (r.status !== 'chase' && r.followUpDate) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['followUpDate'], message: 'A follow-up date applies only to Chase.' });
    }
});
exports.AwaitingPaymentAddNoteRequestSchema = zod_1.z.object({
    action: zod_1.z.literal('addNote'),
    companyId: zod_1.z.string().min(1),
    dealId: DealIdSchema,
    note: NoteSchema,
});
/** Longest search text accepted ("Find the payment": client name or loan number). */
exports.AWAITING_PAYMENT_SEARCH_MAX = 80;
/**
 * "Find the payment": the caller's own statement loans, to link one to a deal
 * the matcher could not. Empty query = loans settled near the deal at a similar
 * amount; otherwise every word must appear in the client name, or the text in
 * the loan number. Linking goes through decideDealLoanLink like Confirm payment.
 */
exports.AwaitingPaymentSearchLoansRequestSchema = zod_1.z.object({
    action: zod_1.z.literal('searchLoans'),
    companyId: zod_1.z.string().min(1),
    dealId: DealIdSchema,
    query: zod_1.z.string().trim().max(exports.AWAITING_PAYMENT_SEARCH_MAX).optional(),
});
exports.AwaitingPaymentActionRequestSchema = zod_1.z.union([
    exports.AwaitingPaymentListRequestSchema,
    exports.AwaitingPaymentDetailRequestSchema,
    exports.AwaitingPaymentSetStatusRequestSchema,
    exports.AwaitingPaymentAddNoteRequestSchema,
    exports.AwaitingPaymentSearchLoansRequestSchema,
]);
